import { useState, useMemo } from "react"
import { Modal, Button, Form, Table, Badge, InputGroup } from "react-bootstrap"
import { Search, Package, Filter } from "lucide-react"

const InventoryModal = ({ show, onHide, data }) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("")
  const [selectedDepartment, setSelectedDepartment] = useState("")

  // Filter data based on search term and filters
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const matchesSearch = item.item_name.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesCategory = selectedCategory === "" || item.category === selectedCategory
      const matchesDepartment = selectedDepartment === "" || item.department === selectedDepartment

      return matchesSearch && matchesCategory && matchesDepartment
    })
  }, [data, searchTerm, selectedCategory, selectedDepartment])

  // Get unique categories and departments for filter dropdowns
  const categories = useMemo(() => [...new Set(data.map((item) => item.category))], [data])
  const departments = useMemo(() => [...new Set(data.map((item) => item.department))], [data])

  const getStatusVariant = (status) => {
    switch (status.toLowerCase()) {
      case "normal":
        return "success"
      case "low":
        return "warning"
      case "out of stock":
        return "danger"
      default:
        return "secondary"
    }
  }

  const handleClearFilters = () => {
    setSearchTerm("")
    setSelectedCategory("")
    setSelectedDepartment("")
  }

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="d-flex align-items-center">
          <Package className="me-2" size={24} />
          Inventory Stocks
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0">
        {/* Search and Filter Section */}
        <div className="p-3 border-bottom bg-light">
          <div className="row g-3">
            <div className="col-md-6">
              <InputGroup>
                <InputGroup.Text>
                  <Search size={16} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search by item name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </div>

            <div className="col-md-3">
              <Form.Select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </Form.Select>
            </div>

            <div className="col-md-3">
              <Form.Select value={selectedDepartment} onChange={(e) => setSelectedDepartment(e.target.value)}>
                <option value="">All Departments</option>
                {departments.map((department) => (
                  <option key={department} value={department}>
                    {department}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="text-muted">
              Showing {filteredData.length} of {data.length} items
            </div>
            {(searchTerm || selectedCategory || selectedDepartment) && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleClearFilters}
                className="d-flex align-items-center"
              >
                <Filter size={14} className="me-1" />
                Clear Filters
              </Button>
            )}
          </div>
        </div>

        {/* Table Section */}
        <div style={{ maxHeight: "500px", overflowY: "auto" }}>
          <Table striped hover responsive className="mb-0">
            <thead className="table-dark sticky-top">
              <tr>
                <th>Item Name</th>
                <th>Quantity</th>
                <th>Unit</th>
                <th>Department</th>
                <th>Category</th>
                {/* <th>Status</th> */}
              </tr>
            </thead>
            <tbody>
              {filteredData.length > 0 ? (
                filteredData.map((item) => (
                  <tr key={item.item_id}>
                    <td className="fw-semibold">{item.item_name}</td>
                    <td>
                      <span className={`fw-bold ${item.current_quantity < 10 ? "text-warning" : "text-success"}`}>
                        {item.current_quantity}
                      </span>
                    </td>
                    <td className="text-muted">{item.unit}</td>
                    <td>{item.department}</td>
                    <td>
                      <Badge bg="info" className="text-dark">
                        {item.category}
                      </Badge>
                    </td>
                    {/* <td>
                      <Badge bg={getStatusVariant(item.status)}>{item.status}</Badge>
                    </td> */}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-4 text-muted">
                    <Package size={48} className="mb-2 opacity-50" />
                    <div>No items found matching your search criteria</div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Modal.Body>

      <Modal.Footer className="bg-light">
        <div className="d-flex justify-content-between w-100 align-items-center">
          <div className="text-muted small">
            Total Items: {data.length} | Total Quantity: {data.reduce((sum, item) => sum + item.current_quantity, 0)}
          </div>
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default InventoryModal
